import React from 'react';
import useSWR from 'swr';
import {
  CreateAdCampaignDto,
  CreateAdCampaignInput,
  EAdCreativeAssetStatus,
  EAdCreativeSource,
  IAdCampaign,
  IAdCampaignLead,
  IAdCreativeAsset,
} from 'types/ad-campaign';

import {
  createAdCampaign as createAdCampaignRequest,
  uploadAdCreative,
  UploadProgressCallback,
} from 'utils/api/ad-campaigns';

const AD_CREATIVE_POLL_INTERVAL_MS = 2000;
const AD_CREATIVE_POLL_TIMEOUT_MS = 10 * 60 * 1000;

const isTerminalCreativeAssetStatus = (status?: EAdCreativeAssetStatus) => [
  EAdCreativeAssetStatus.Ready,
  EAdCreativeAssetStatus.Failed,
  EAdCreativeAssetStatus.Expired,
].includes(status as EAdCreativeAssetStatus);

export const useAdCampaigns = () => {
  const { data, error } = useSWR('/digital-ads');

  return {
    adCampaigns: data?.data as IAdCampaign[],
    isLoadingAdCampaigns: !data,
    isErrorAdCampaigns: !!error,
  };
};

export const useAdCampaignDetail = (adCampaignId: string) => {
  const { data, error, mutate } = useSWR(`/digital-ads/${adCampaignId}`);

  return {
    adCampaign: data?.data as IAdCampaign,
    isLoadingAdCampaign: !data,
    isErrorAdCampaign: !!error,
    mutateAdCampaign: mutate,
  };
};

export const useAdCampaignLeads = (adCampaignId: string) => {
  const { data, error } = useSWR(`/digital-ads/${adCampaignId}/leads`);

  return {
    leads: data?.data as IAdCampaignLead[],
    isLoadingLeads: !data,
    isErrorLeads: !!error,
  };
};

export const useAdCampaignAdPreview = (fetch = true) => {
  const { data, error } = useSWR(fetch ? '/digital-ads/ad-preview' : null);

  return {
    adPreview: data?.data as string,
    isLoadingAdPreview: fetch && !data,
    isErrorAdPreview: !!error,
  };
};

export const useAdCreativeAsset = (assetId?: string) => {
  const trackedAssetId = React.useRef<string>();
  const pollStartedAt = React.useRef<number>();
  const [hasTimedOut, setHasTimedOut] = React.useState(false);

  if (trackedAssetId.current !== assetId) {
    trackedAssetId.current = assetId;
    pollStartedAt.current = assetId ? Date.now() : undefined;
  }

  React.useEffect(() => {
    setHasTimedOut(false);
  }, [assetId]);

  const pollHasTimedOut = () => (
    !!pollStartedAt.current
    && Date.now() - pollStartedAt.current >= AD_CREATIVE_POLL_TIMEOUT_MS
  );

  const { data, error } = useSWR(
    assetId ? `/digital-ads/creative-assets/${assetId}` : null,
    {
      refreshInterval: (latestData) => (
        hasTimedOut
        || pollHasTimedOut()
        || isTerminalCreativeAssetStatus(latestData?.data?.status)
          ? 0
          : AD_CREATIVE_POLL_INTERVAL_MS
      ),
      onSuccess: () => {
        if (pollHasTimedOut()) setHasTimedOut(true);
      },
    },
  );

  return {
    creativeAsset: data?.data as IAdCreativeAsset | undefined,
    isLoadingCreativeAsset: !!assetId && !data && !error,
    isErrorCreativeAsset: !!error,
    creativeAssetError: error,
    hasCreativeAssetTimedOut: hasTimedOut,
  };
};

type PendingAdCampaignCreation = {
  input: CreateAdCampaignInput;
  resolve: (adCampaignId: string) => void;
  reject: (reason: any) => void;
};

type CreateAdCampaignOptions = {
  onUploadProgress?: UploadProgressCallback;
};

const getCreateAdCampaignDto = (
  input: CreateAdCampaignInput,
  assetId?: string,
): CreateAdCampaignDto => ({
  ...input,
  creative: input.creative.source === EAdCreativeSource.UserUpload
    ? {
      source: EAdCreativeSource.UserUpload,
      assetId: assetId!,
    }
    : {
      source: EAdCreativeSource.HarvistGenerated,
    },
});

export const useCreateAdCampaign = () => {
  const [assetId, setAssetId] = React.useState<string>();
  const pendingCreation = React.useRef<PendingAdCampaignCreation>();
  const {
    creativeAsset,
    creativeAssetError,
    hasCreativeAssetTimedOut,
  } = useAdCreativeAsset(assetId);

  const clearPendingCreation = () => {
    pendingCreation.current = undefined;
    setAssetId(undefined);
  };

  React.useEffect(() => {
    const pending = pendingCreation.current;
    if (!pending) return;

    if (creativeAssetError) {
      clearPendingCreation();
      pending.reject(creativeAssetError);
      return;
    }

    if (hasCreativeAssetTimedOut) {
      clearPendingCreation();
      pending.reject(new Error('Creative processing took too long. Please try again.'));
      return;
    }

    if (!creativeAsset || !isTerminalCreativeAssetStatus(creativeAsset.status)) return;

    clearPendingCreation();

    if (creativeAsset.status !== EAdCreativeAssetStatus.Ready) {
      pending.reject(new Error(
        creativeAsset.errorMessage || 'We were unable to process this creative.',
      ));
      return;
    }

    createAdCampaignRequest(getCreateAdCampaignDto(pending.input, creativeAsset._id))
      .then(pending.resolve)
      .catch(pending.reject);
  }, [creativeAsset, creativeAssetError, hasCreativeAssetTimedOut]);

  const createAdCampaign = React.useCallback(async (
    input: CreateAdCampaignInput,
    options: CreateAdCampaignOptions = {},
  ) => {
    if (pendingCreation.current) {
      throw new Error('An ad campaign is already being created.');
    }

    if (input.creative.source === EAdCreativeSource.HarvistGenerated) {
      return createAdCampaignRequest(getCreateAdCampaignDto(input));
    }

    const asset = await uploadAdCreative(
      input.creative.file,
      input.creative.mediaType,
      options.onUploadProgress,
    );

    if (asset.status === EAdCreativeAssetStatus.Ready) {
      return createAdCampaignRequest(getCreateAdCampaignDto(input, asset._id));
    }

    if (isTerminalCreativeAssetStatus(asset.status)) {
      throw new Error(asset.errorMessage || 'We were unable to process this creative.');
    }

    return new Promise<string>((resolve, reject) => {
      pendingCreation.current = {
        input,
        resolve,
        reject,
      };
      setAssetId(asset._id);
    });
  }, []);

  return {
    createAdCampaign,
    creativeAsset,
  };
};
